import React, { useCallback, useEffect, useMemo, useState } from 'react';
import JSONPretty from 'react-json-pretty';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { RootState } from '../../redux/store';
import capitalizeFirstLetter from '../../shared-functions/StringFunctions';
import ocppLogsApi from '../../utils/api/ocpp-logs';

/**
 * Defines the panel behavior type.
 */
type PanelType = 'station' | 'log';

/**
 * Side panel component props.
 */
interface OcppLogSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  panelType?: PanelType;
}

/**
 * Parsed OCPP message structure.
 */
interface OcppMessageProperties {
  messageTypeId: number | string;
  uniqueId: string;
  action?: string;
  payload: Record<string, unknown>;
}

/**
 * Common OCPP Log Side Panel Component
 *
 * - Defaults to `log` mode if panelType is not provided
 * - Supports station and log behaviors
 *
 * @param props Component props
 * @returns JSX.Element
 */
function OcppLogSidePanel({
  fetchIdFrom,
  id,
  panelType = 'log',
}: OcppLogSidePanelProperties): JSX.Element {
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const { stationsActivity } = useAppSelector(
    (state: RootState) => state.stationActivity,
  );
  const { ocppLogs } = useAppSelector(
    (state: RootState) => state.ocppLogs,
  );

  const [ocppLogData, setOcppLogData] = useState<any | null>(null);
  const [dataToDisplay, setDataToDisplay] =
    useState<OcppMessageProperties | null>(null);

  /**
   * Query param key based on panel type.
   */
  const queryKey = useMemo(
    () => (panelType === 'station' ? 'stationId' : 'logId'),
    [panelType],
  );

  /**
   * Parses OCPP message JSON string safely.
   *
   * @param messageString Raw OCPP message string
   */
  const parseOcppMessage = useCallback((messageString: string): void => {
    try {
      const parsed = JSON.parse(messageString) as [
        number,
        string,
        string | Record<string, unknown>,
        Record<string, unknown>?,
      ];

      const [messageTypeId, uniqueId, third, fourth] = parsed;

      if (typeof third === 'object') {
        setDataToDisplay({
          messageTypeId,
          uniqueId,
          payload: third,
        });
      } else {
        setDataToDisplay({
          messageTypeId,
          uniqueId,
          action: third,
          payload: fourth ?? {},
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Invalid OCPP message format', error);
    }
  }, []);

  /**
   * Fetches log data based on panel type.
   *
   * @param logId Log identifier
   */
  const fetchLogDetails = useCallback(
    async (logId: string): Promise<void> => {
      let logData: any | undefined;

      if (panelType === 'station') {
        logData = stationsActivity.find(
          (log) => log.id === logId,
        );
      } else {
        logData = ocppLogs.find((log) => log.id === logId);

        if (!logData) {
          try {
            const response = await ocppLogsApi.getLogById(logId);
            logData = response.data;
          } catch (error: any) {
            dispatch(
              setNotificationMessage({
                status: true,
                message:
                  error?.response?.data?.message ||
                  'Something went wrong!',
                type: 'error',
                code: 0,
              }),
            );
          }
        }
      }

      if (!logData) {
        searchParameters.delete(queryKey);
        setSearchParameters(searchParameters);
        return;
      }

      setOcppLogData(logData);
      parseOcppMessage(logData.message);
    },
    [
      dispatch,
      ocppLogs,
      panelType,
      parseOcppMessage,
      queryKey,
      searchParameters,
      setSearchParameters,
      stationsActivity,
    ],
  );

  /**
   * Resolve log id source and trigger fetch.
   */
  useEffect(() => {
    const resolvedId =
      fetchIdFrom === 'queryParams'
        ? searchParameters.get(queryKey)
        : id;

    if (!resolvedId) {
      return;
    }

    fetchLogDetails(resolvedId);
  }, [
    fetchIdFrom,
    fetchLogDetails,
    id,
    queryKey,
    searchParameters,
  ]);

  return (
    <div className="ocpp-logs-side-panel-wrap">
      <div className="each-log-data">
        <h6>Message Type</h6>
        <p className={ocppLogData?.type}>
          {capitalizeFirstLetter(ocppLogData?.type || '-')}
        </p>
      </div>

      <div className="each-log-data">
        <h6>Message Type Id</h6>
        <p>{dataToDisplay?.messageTypeId ?? '-'}</p>
      </div>

      <div className="each-log-data">
        <h6>Unique Id</h6>
        <p>{dataToDisplay?.uniqueId ?? '-'}</p>
      </div>

      <div className="each-log-data">
        <h6>Action</h6>
        <p>{ocppLogData?.action ?? '-'}</p>
      </div>

      <div className="each-log-data payload">
        <h6>Payload :</h6>
        <JSONPretty
          id="json-pretty"
          data={dataToDisplay?.payload ?? {}}
        />
      </div>
    </div>
  );
}

OcppLogSidePanel.defaultProps = {
  panelType: 'log',
  id:'',
};

export default OcppLogSidePanel;
